/**
 * nav-config.ts — sidebar navigation data configuration.
 *
 * Pure data + types + one small helper. NO React, NO JSX, NO side effects.
 * The AppShell consumes these arrays to render <Sidebar*> DS components.
 *
 * All `href` values are relative to the workspace/org prefix — the shell
 * prepends `/w/:slug/app/` (workspace) or `/o/:orgSlug/` (org) at render
 * time. Icon strings are identifiers mapped to SVG in the shell layer.
 *
 * Every href (and every App.tsx workspace route) is checked by
 * nav-config.test.ts against the route table in App.tsx (V98-3-1).
 */

import { activeProgramKeys, getProgramManifest, programsWhere, type ProgramKey, type ProgramManifest } from '@aigovops/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NavRole = 'owner' | 'admin' | 'editor' | 'viewer';
export type SessionMode = 'customer' | 'assessor' | 'staff';

export interface NavItem {
  id: string;
  label: string;
  /** Relative to workspace prefix, e.g. 'register' */
  href: string;
  /** Icon identifier — mapped to SVG in the shell */
  icon: string;
  /** Dynamic badge source (shell resolves to a count at runtime) */
  badge?: 'alerts' | 'my-work' | 'evidence-requests' | 'disputes' | 'questionnaires' | 'suppliers' | 'monitoring';
  disabled?: boolean;
  disabledLabel?: string;
  /** Route patterns that activate this item (besides exact href match) */
  matchPatterns?: string[];
  /** Render as a sub-item of the entry above it (e.g. Boundary under Systems). */
  indent?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
  /** If set, only these roles see the group */
  roles?: NavRole[];
  /** Default expanded? (Usually false — Overview is the exception) */
  defaultExpanded?: boolean;
  /** Paths that belong to the group itself (e.g. the Programs index). */
  matchPatterns?: string[];
}

export interface NavTopLevel extends NavItem {
  /** Marker — renders as SidebarTopLevel, not inside a group */
  topLevel: true;
}

export interface NavActionItem {
  id: string;
  label: string;
  icon: string;
  /** Route to navigate to, relative to workspace prefix */
  href: string;
}

// ---------------------------------------------------------------------------
// Workspace navigation (V98-3-1 · PRD G-1, G-2, G-8 · D-98-1, D-98-7)
// ---------------------------------------------------------------------------
// Today · Programs (one row per program from /programs) · Systems (+ Boundary)
// · Evidence · Policies · Calendar & trust center · Team & assignments ·
// Settings. The Classic and Lifecycle sidebars are retired: the AI-system
// lifecycle lives on the system page, and every route they listed stays
// reachable — through a nav item's `matchPatterns` (so the shell highlights
// the parent) and through that parent's section links (`WORKSPACE_SECTIONS`),
// which the shell renders as a link strip on every page of the section.

/** Certification stage keys (mirrors the design-system CertStage enum). */
export type NavCertStage = 'scope' | 'prepare' | 'audit' | 'operate' | 'report';

/** A certification program row in the sidebar — derived from GET /programs. */
export interface ProgramNavPeriod {
  /** Period label ("2026", "Standing", "Not started"). */
  label: string;
  /** Stage keys for <StageChip> — never free text. */
  stages: NavCertStage[];
}

export interface ProgramNavItem extends NavItem {
  program: string;
  /** Active periods with their stage chips (empty for an absent program). */
  periods: ProgramNavPeriod[];
  /** The program is not set up yet — the row opens its "Add program" state. */
  absent?: boolean;
}

/** Display names for program keys, from the program manifest (ADR-0050). Active programs only. */
export const PROGRAM_LABELS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(programsWhere(() => true).map((m) => [m.key, m.label])),
);

/** Active program keys in display order (manifest `order`; planned programs never show). */
export const PROGRAM_ORDER: readonly ProgramKey[] = Object.freeze(activeProgramKeys() as ProgramKey[]);
export type { ProgramKey };

/** True for a program a workspace can have today (an active manifest). */
export function isProgramKey(v: unknown): v is ProgramKey {
  return typeof v === 'string' && PROGRAM_ORDER.includes(v as ProgramKey);
}

/**
 * The program significant change notices belong to (manifest
 * `features.significantChangeNotices`: FedRAMP) — the fallback key for SCN
 * and package links whose card isn't loaded.
 */
export const SCN_PROGRAM: ProgramKey | null = (programsWhere((m) => m.features.significantChangeNotices)[0]?.key ?? null) as ProgramKey | null;

/** Minimal shape of a program card the nav needs (a subset of core `ProgramCard`). */
export interface ProgramNavSource {
  program: string;
  report_type?: string | null;
  periods: Array<{ label: string; stage: 'scope' | 'prepare' | 'audit' | 'certified'; concurrent: Array<'operate' | 'report'>; active: boolean }>;
}

/** Program title: "SOC 2 Type II", "FedRAMP 20x", … (manifest label + `nav.titleSuffix`). */
export function programTitle(program: string | ProgramManifest, reportType?: string | null): string {
  const m = typeof program === 'string' ? getProgramManifest(program) : program;
  if (!m) return program as string;
  const suffix = m.nav.titleSuffix({ report_type: reportType ?? null });
  return suffix ? `${m.label} ${suffix}` : m.label;
}

/** Stage chips for one period: its stage, plus Operate when it runs alongside (AI Regulatory in force). */
export function periodStages(p: ProgramNavSource['periods'][number]): NavCertStage[] {
  if (p.stage === 'certified') return ['operate'];
  return p.concurrent.includes('operate') ? [p.stage, 'operate'] : [p.stage];
}

/**
 * Sidebar rows for the Programs group: every active program the workspace
 * has, `nav.showWhenAbsent` ones as "Add program", and a planned program only
 * when GET /programs returned its card (the server is the authority).
 */
export function programNavItems(programs: ProgramNavSource[]): ProgramNavItem[] {
  const byKey = new Map(programs.map((p) => [p.program, p] as const));
  const items: ProgramNavItem[] = [];
  // V102-8-1: a planned program (CMMC) shows only when the server returned its card (staging preview);
  // it is never offered as "Add program" from the nav.
  for (const m of programsWhere(() => true, { planned: true })) {
    const key = m.key;
    const p = byKey.get(key);
    if (!p && (m.lifecycle !== 'active' || !m.nav.showWhenAbsent)) continue;
    items.push({
      id: `program-${key}`,
      label: p ? programTitle(m, p.report_type) : m.label,
      href: `programs/${key}`,
      icon: 'layers',
      matchPatterns: [`programs/${key}/*`, ...m.nav.legacyRoutes],
      program: key,
      periods: p ? p.periods.filter((x) => x.active).map((x) => ({ label: x.label, stages: periodStages(x) })) : [],
      absent: !p,
    });
  }
  return items;
}

/** Links shown as a strip on every page of a nav section (no orphan pages). */
export interface SectionLink {
  id: string;
  label: string;
  href: string;
  matchPatterns?: string[];
}

export const WORKSPACE_SECTIONS: Record<string, SectionLink[]> = {
  today: [
    { id: 'today', label: 'Today', href: 'today', matchPatterns: ['dashboard', 'items/*', 'responses/*'] },
    { id: 'mine', label: 'My items', href: 'mine' },
    { id: 'alerts', label: 'Alerts', href: 'alerts', matchPatterns: ['alerts/*'] },
    { id: 'findings', label: 'Findings', href: 'findings' },
    { id: 'incidents', label: 'Incidents', href: 'incidents', matchPatterns: ['incidents/*'] },
    { id: 'exceptions', label: 'Risk acceptances', href: 'exceptions' },
    { id: 'roadmap', label: 'Roadmap', href: 'roadmap' },
  ],
  systems: [
    { id: 'systems', label: 'Systems', href: 'systems', matchPatterns: ['systems/*', 'plan', 'build', 'release', 'operate', 'sell'] },
    { id: 'use-cases', label: 'Use cases', href: 'use-cases', matchPatterns: ['use-cases/*'] },
    { id: 'discovered-tools', label: 'Discovered tools', href: 'discovered-tools' },
    { id: 'suppliers', label: 'Suppliers', href: 'suppliers', matchPatterns: ['suppliers/*'] },
    { id: 'blueprint', label: 'Blueprint', href: 'blueprint' },
    { id: 'setup', label: 'Setup', href: 'setup', matchPatterns: ['settings/integrations'] }, // V108-8-20: the page's own title (was "Connections")
  ],
  evidence: [
    { id: 'evidence', label: 'Evidence', href: 'evidence' },
    { id: 'evidence-requests', label: 'Requests to you', href: 'evidence-requests' },
    { id: 'register', label: 'Register', href: 'register', matchPatterns: ['register/*'] },
    { id: 'common-controls', label: 'Common controls', href: 'common-controls' },
    { id: 'library-tests', label: 'Test library', href: 'library/tests' },
    { id: 'audit', label: 'Audit log', href: 'audit', matchPatterns: ['audit/events/*'] },
    { id: 'audit-settings', label: 'Audit settings', href: 'audit/settings' },
  ],
  calendar: [
    // V98-5-6: the calendar + trust center page leads the section.
    { id: 'calendar', label: 'Calendar & trust center', href: 'calendar' },
    { id: 'deliverables', label: 'Reports & packages', href: 'deliverables', matchPatterns: ['reports'] },
    // V111-5-5 (ADR-0094): the signed AI insurability pack.
    { id: 'insurability', label: 'Insurability pack', href: 'insurability' },
    { id: 'releases', label: 'Releases', href: 'releases' },
    { id: 'questionnaires', label: 'Questionnaires', href: 'questionnaires', matchPatterns: ['questionnaires/*'] },
    { id: 'attested-capabilities', label: 'Vendor attestations', href: 'attested-capabilities' },
  ],
};

/** Every relative path a section covers (its links' hrefs + patterns). */
function sectionPatterns(id: string): string[] {
  return (WORKSPACE_SECTIONS[id] ?? []).flatMap((l) => [l.href, ...(l.matchPatterns ?? [])]);
}

/**
 * V112-3-1 (BL-40 item 1): while any active program isn't started, the Programs group ends with "Add program",
  * which opens the Programs page (`#add`, so the group itself stays the active row there), where every startable program has its Add action (GovRAMP, FedRAMP and the rest,
 * not only the one manifest program shown when absent).
 */
export function addProgramLink(programs: ProgramNavSource[]): NavItem[] {
  const have = new Set(programs.map((p) => p.program));
  return PROGRAM_ORDER.some((k) => !have.has(k))
    ? [{ id: 'programs-add', label: 'Add program', href: 'programs#add', icon: 'plus' }]
    : [];
}

/** Static workspace nav (everything except the dynamic Programs rows). */
export const WORKSPACE_NAV_STATIC: Record<'today' | 'systems' | 'boundary' | 'monitoring' | 'evidence' | 'policies' | 'calendar' | 'team' | 'settings', NavTopLevel> = {
  today: { id: 'today', label: 'Today', href: 'today', icon: 'home', badge: 'my-work', topLevel: true, matchPatterns: sectionPatterns('today') },
  systems: { id: 'systems', label: 'Systems', href: 'systems', icon: 'cpu', topLevel: true, matchPatterns: sectionPatterns('systems') },
  boundary: { id: 'boundary', label: 'Boundary', href: 'boundary', icon: 'grid', topLevel: true, indent: true, matchPatterns: ['programs/*/scope', 'programs/*/scope/*'] },
  // V113-7-2: top-level Monitoring (badge = open monitoring findings).
  monitoring: { id: 'monitoring', label: 'Monitoring', href: 'monitoring', icon: 'activity', badge: 'monitoring', topLevel: true, matchPatterns: ['monitoring/*'] },
  evidence: { id: 'evidence', label: 'Evidence', href: 'evidence', icon: 'paperclip', topLevel: true, matchPatterns: sectionPatterns('evidence') },
  policies: { id: 'policies', label: 'Policies', href: 'policies', icon: 'file-text', topLevel: true },
  calendar: { id: 'calendar', label: 'Calendar & trust center', href: 'calendar', icon: 'calendar', topLevel: true, matchPatterns: sectionPatterns('calendar') },
  team: { id: 'team', label: 'Team & assignments', href: 'team', icon: 'users', topLevel: true, matchPatterns: ['team/*'] },
  settings: { id: 'settings', label: 'Settings', href: 'settings', icon: 'settings', topLevel: true, matchPatterns: ['settings/*', 'audit-firms/*'] },
};

/** Full workspace shell: Today · Programs · Systems (+ Boundary) · Monitoring · Evidence · Policies · Calendar & trust center · Team & assignments · Settings. */
export function workspaceNav(programs: ProgramNavSource[]): (NavGroup | NavTopLevel)[] {
  const s = WORKSPACE_NAV_STATIC;
  return [
    s.today,
    { id: 'programs', label: 'Programs', defaultExpanded: true, items: [...programNavItems(programs), ...addProgramLink(programs)], matchPatterns: ['programs'] },
    s.systems, s.boundary, s.monitoring, s.evidence, s.policies, s.calendar, s.team, s.settings,
  ];
}

/**
 * Minimal shell for a viewer whose only hats are owner-type (D-98-15,
 * research §2 rule 11): their items, and Settings. No program, team or
 * evidence surfaces they would have to filter themselves.
 */
export const MINIMAL_NAV: (NavGroup | NavTopLevel)[] = [
  // V98-3 review U7: My items IS Today for an owner-only viewer (Today composes the minimal view from /today).
  { id: 'mine', label: 'My items', href: 'today', icon: 'user-check', badge: 'my-work', topLevel: true, matchPatterns: ['mine', 'dashboard', 'items/*', 'responses/*'] },
  { ...WORKSPACE_NAV_STATIC.settings },
];

/** Hats that only own work (fix / evidence their own items) — research §2 rule 11. */
export const OWNER_TYPE_HATS = ['owner', 'system_owner'] as const;

/**
 * Viewer-scoped shell (no role switcher anywhere): the minimal shell when
 * the viewer is not an owner/admin, leads no program, and every hat they
 * hold is owner-type. Anyone with no hats yet keeps the full shell.
 */
export function isOwnerOnlyViewer(v: { hats: string[]; lead_program_ids: string[]; is_admin: boolean } | null | undefined): boolean {
  if (!v || v.is_admin || v.lead_program_ids.length > 0 || v.hats.length === 0) return false;
  return v.hats.every((h) => (OWNER_TYPE_HATS as readonly string[]).includes(h));
}

/** The section (and its link) the path belongs to, for the section strip. */
export function findSection(relativePath: string): { sectionId: string; linkId: string } | null {
  const path = relativePath.split('?')[0]!;
  for (const pass of ['exact', 'pattern'] as const) {
    for (const [sectionId, links] of Object.entries(WORKSPACE_SECTIONS)) {
      for (const l of links) {
        const hit = pass === 'exact' ? path === l.href : (l.matchPatterns ?? []).some((p) => matchWildcard(p, path));
        if (hit) return { sectionId, linkId: l.id };
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Staff editorial items (appended when user.role === 'admin')
// ---------------------------------------------------------------------------

export const STAFF_NAV_GROUP: NavGroup = {
  id: 'editorial',
  label: 'Editorial',
  roles: ['admin' as NavRole], // site-level admin, not workspace admin
  items: [
    { id: 'queue', label: 'Editorial Queue', href: 'queue', icon: 'inbox' },
    { id: 'sources', label: 'Sources', href: 'sources', icon: 'database' },
  ],
};

// ---------------------------------------------------------------------------
// Assessor navigation
// ---------------------------------------------------------------------------

export const ASSESSOR_NAV_GROUPS: (NavGroup | NavTopLevel)[] = [
  {
    id: 'overview',
    label: 'Overview',
    defaultExpanded: true,
    items: [
      { id: 'dashboard', label: 'Dashboard', href: 'dashboard', icon: 'home' },
    ],
  },
  {
    id: 'assessment',
    label: 'Assessment',
    defaultExpanded: true,
    items: [
      { id: 'audit-log', label: 'Audit Log', href: 'audit', icon: 'clock', matchPatterns: ['audit/events/*'] },
      // V98-3-1: 'assessor/workpaper' resolves to the engagement in the scope bar (App.tsx index route).
      { id: 'workpapers', label: 'Workpapers', href: 'assessor/workpaper', icon: 'file', matchPatterns: ['assessor/workpaper/*'] },
      { id: 'evidence-library', label: 'Evidence Library', href: 'evidence', icon: 'paperclip' },
      { id: 'evidence-requests', label: 'Evidence Requests', href: 'evidence-requests', icon: 'mail', badge: 'evidence-requests' },
      // V98-3-1: "Assessment Notes" (assessor-annotations) removed — no such page; annotations live on
      // each register item (AssessorAnnotationsSection). Track 6 replaces this nav.
      { id: 'disputes', label: 'Disputes', href: 'settings/disputes', icon: 'alert-circle', badge: 'disputes' },
    ],
  },
  {
    id: 'deliverables',
    label: 'Deliverables',
    defaultExpanded: true,
    items: [
      { id: 'signed-packages', label: 'Signed Packages', href: 'deliverables', icon: 'package' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Organization navigation
// ---------------------------------------------------------------------------

export const ORG_NAV_GROUPS: (NavGroup | NavTopLevel)[] = [
  {
    id: 'organization',
    label: 'Organization',
    defaultExpanded: true,
    items: [
      // V98-3-1: 'workspaces' removed (no page; the workspace switcher lists them).
      { id: 'org-dashboard', label: 'Org Dashboard', href: 'dashboard', icon: 'home' },
    ],
  },
  {
    id: 'third-party-risk',
    label: 'Third-Party Risk',
    defaultExpanded: true,
    items: [
      { id: 'ai-systems', label: 'AI Systems', href: 'ai-systems', icon: 'cpu' },
      { id: 'suppliers', label: 'Suppliers', href: 'suppliers', icon: 'briefcase', badge: 'suppliers', matchPatterns: ['suppliers/*'] },
      // V98-3-1: org 'discovered-tools' removed (no org page; discovery is per workspace).
      { id: 'vendor-attestations', label: 'Vendor Attestations', href: 'vendor-attestations', icon: 'award' },
      { id: 'disclosure-requests', label: 'Disclosure Requests', href: 'disclosure-requests', icon: 'send' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    defaultExpanded: true,
    items: [
      // V98-3-1: 'readiness' removed (no page; the Org Dashboard carries the aggregate).
      { id: 'findings', label: 'Findings', href: 'findings', icon: 'flag' },
      { id: 'incidents', label: 'Incidents', href: 'incidents', icon: 'alert-triangle' },
    ],
  },
  // Alerts — top-level
  {
    id: 'alerts',
    label: 'Alerts',
    href: 'alerts',
    icon: 'bell',
    badge: 'alerts',
    topLevel: true,
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { id: 'org-settings', label: 'Org Settings', href: 'settings', icon: 'settings' },
    ],
  },
];

// ---------------------------------------------------------------------------
// "+ New" actions
// ---------------------------------------------------------------------------

export const NEW_ACTIONS: NavActionItem[] = [
  // V112-3-1 (BL-40 item 1): the two setup actions people looked for in "+ New".
  { id: 'add-system', label: 'Add system', icon: 'plus', href: 'systems/new' },
  { id: 'add-program', label: 'Add program', icon: 'plus', href: 'programs#add' },
  { id: 'upload-questionnaire', label: 'Upload questionnaire', icon: 'upload', href: 'questionnaires/new' },
  { id: 'add-evidence', label: 'Add evidence', icon: 'paperclip', href: 'evidence?new=upload' },
  { id: 'log-incident', label: 'Log incident', icon: 'alert-triangle', href: 'incidents?action=new' },
  { id: 'export-readiness', label: 'Export readiness report', icon: 'download', href: 'deliverables' },
];

// ---------------------------------------------------------------------------
// Settings items with role filtering
// ---------------------------------------------------------------------------

export interface SettingsNavItem {
  id: string;
  label: string;
  /** Relative to workspace prefix + '/settings/' */
  href: string;
  /** Grouping key: 'account' | 'workspace' | 'security' | 'audit' | 'billing' */
  section: string;
  /** If set, only this role and above see the item */
  minRole?: NavRole;
}

export const SETTINGS_NAV_ITEMS: SettingsNavItem[] = [
  // Account
  { id: 'profile', label: 'Profile', href: 'profile', section: 'account' },
  // Workspace (editor+)
  { id: 'compliance', label: 'Targets & Frameworks', href: 'compliance', section: 'workspace', minRole: 'editor' },
  { id: 'integrations', label: 'Integrations', href: 'integrations', section: 'workspace', minRole: 'admin' },
  { id: 'control-specs', label: 'Control Specs', href: 'control-specs', section: 'workspace', minRole: 'admin' },
  { id: 'coverage-regions', label: 'Coverage regions', href: 'coverage-regions', section: 'workspace', minRole: 'editor' },
  { id: 'signatures', label: 'Signature routing', href: 'signatures', section: 'workspace', minRole: 'admin' },
  { id: 'signing', label: 'Signing', href: 'signing', section: 'security', minRole: 'admin' },
  // Security (admin+)
  { id: 'sso', label: 'SSO', href: 'sso', section: 'security', minRole: 'admin' },
  { id: 'security', label: 'Security', href: 'security', section: 'security', minRole: 'admin' },
  { id: 'api', label: 'API Tokens', href: 'api', section: 'security', minRole: 'admin' },
  // Panel H8: the two webhook pages are named by what they carry, and cross-link each other.
  { id: 'webhooks', label: 'Audit log webhooks', href: 'webhooks', section: 'security', minRole: 'admin' },
  // V114-5-2: outbound notification webhooks (finding opened/closed + pack validity; D-114-11).
  { id: 'event-webhooks', label: 'Notification webhooks', href: 'event-webhooks', section: 'security', minRole: 'admin' },
  // Audit (admin+)
  { id: 'audit-firms', label: 'Audit firms', href: 'audit-firms', section: 'audit', minRole: 'viewer' },
  { id: 'assessors', label: 'Assessor Engagements', href: 'assessors', section: 'audit', minRole: 'admin' },
  { id: 'disputes', label: 'Disputes', href: 'disputes', section: 'audit', minRole: 'admin' },
  // Billing (owner)
  { id: 'billing', label: 'Billing', href: 'billing', section: 'billing', minRole: 'owner' },
];

const ROLE_HIERARCHY: NavRole[] = ['viewer', 'editor', 'admin', 'owner'];

/** Filter settings items visible to a given role. */
export function visibleSettings(role: NavRole): SettingsNavItem[] {
  const roleIdx = ROLE_HIERARCHY.indexOf(role);
  return SETTINGS_NAV_ITEMS.filter((item) => {
    if (!item.minRole) return true;
    return roleIdx >= ROLE_HIERARCHY.indexOf(item.minRole);
  });
}

// ---------------------------------------------------------------------------
// Active-item resolution
// ---------------------------------------------------------------------------

/**
 * Given a pathname relative to the workspace prefix (e.g. 'register/abc123'),
 * find the nav item that should be marked active.
 */
export function findActiveItem(
  groups: (NavGroup | NavTopLevel)[],
  relativePath: string,
): { groupId: string; itemId: string } | null {
  const path = relativePath.split('?')[0]!;
  // Exact href matches win over patterns anywhere in the nav (e.g.
  // 'settings/team' is Team & assignments, not the Settings wildcard).
  for (const pass of ['exact', 'pattern'] as const) {
    for (const entry of groups) {
      const hit = (item: { href: string; matchPatterns?: string[] }) =>
        pass === 'exact' ? path === item.href.split('?')[0] : !!item.matchPatterns?.some((p) => matchWildcard(p, path));
      if ('topLevel' in entry && entry.topLevel) {
        if (hit(entry)) return { groupId: entry.id, itemId: entry.id };
        continue;
      }
      const group = entry as NavGroup;
      for (const item of group.items) {
        if (hit(item)) return { groupId: group.id, itemId: item.id };
      }
      if (pass === 'pattern' && group.matchPatterns?.some((p) => p === path || matchWildcard(p, path))) {
        return { groupId: group.id, itemId: group.id };
      }
    }
  }
  return null;
}

function matchWildcard(pattern: string, path: string): boolean {
  if (pattern.endsWith('/*')) {
    return path.startsWith(pattern.slice(0, -1));
  }
  return pattern === path;
}
